import React, { useState } from 'react';

const inputClasses = "input-premium py-2.5 text-sm";

function AdvancedFilter({ searchPlaceholder, filters, onFilterChange, activeCount }) {
  const [expanded, setExpanded] = useState(false);

  const handleClear = () => {
    filters.forEach((f) => {
      if (f.type === 'search') onFilterChange(f.key, '');
      else if (f.type === 'select') onFilterChange(f.key, f.defaultValue ?? 'all');
      else if (f.type === 'dateRange') onFilterChange(f.key, { from: '', to: '' });
      else if (f.type === 'numberRange') onFilterChange(f.key, { min: '', max: '' });
      else if (f.type === 'sort') onFilterChange(f.key, f.defaultValue ?? '');
    });
  };

  const hasActiveFilters = activeCount > 0;

  return (
    <div className="card-premium overflow-hidden">
      {/* Main search + toggle row */}
      <div className="p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={filters.find((f) => f.type === 'search')?.value ?? ''}
            onChange={(e) => {
              const searchFilter = filters.find((f) => f.type === 'search');
              if (searchFilter) onFilterChange(searchFilter.key, e.target.value);
            }}
            className={`${inputClasses} w-full pl-11`}
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasActiveFilters && (
            <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium">
              {activeCount} active
            </span>
          )}
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-premium border font-medium text-sm transition-all ${
              expanded || hasActiveFilters
                ? 'border-amber-300 bg-amber-50 text-amber-700'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            <svg
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2.5 rounded-premium border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium text-sm transition-all"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Expanded advanced filters */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-100">
          <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filters
              .filter((f) => f.type !== 'search')
              .map((filter) => (
                <div key={filter.key}>
                  {filter.type === 'select' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">{filter.label}</label>
                      <select
                        value={filter.value ?? filter.defaultValue ?? 'all'}
                        onChange={(e) => onFilterChange(filter.key, e.target.value)}
                        className={`${inputClasses} w-full`}
                      >
                        <option value="all">{filter.placeholder || `All ${filter.label}`}</option>
                        {filter.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {filter.type === 'sort' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">{filter.label}</label>
                      <select
                        value={filter.value ?? filter.defaultValue ?? ''}
                        onChange={(e) => onFilterChange(filter.key, e.target.value)}
                        className={`${inputClasses} w-full`}
                      >
                        <option value="">Default</option>
                        {filter.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {filter.type === 'dateRange' && (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-slate-500">{filter.label}</label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="date"
                            value={filter.value?.from ?? ''}
                            onChange={(e) =>
                              onFilterChange(filter.key, {
                                ...(filter.value || {}),
                                from: e.target.value,
                              })
                            }
                            className={`${inputClasses} pr-10`}
                            placeholder="dd-mm-yyyy"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </span>
                        </div>
                        <div className="flex-1 relative">
                          <input
                            type="date"
                            value={filter.value?.to ?? ''}
                            onChange={(e) =>
                              onFilterChange(filter.key, {
                                ...(filter.value || {}),
                                to: e.target.value,
                              })
                            }
                            className={`${inputClasses} pr-10`}
                            placeholder="dd-mm-yyyy"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {filter.type === 'numberRange' && (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-slate-500">{filter.label}</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={filter.value?.min ?? ''}
                          onChange={(e) =>
                            onFilterChange(filter.key, {
                              ...(filter.value || {}),
                              min: e.target.value,
                            })
                          }
                          className={`${inputClasses} flex-1`}
                          placeholder={filter.minPlaceholder || 'Min'}
                          min={filter.min}
                          max={filter.max}
                        />
                        <input
                          type="number"
                          value={filter.value?.max ?? ''}
                          onChange={(e) =>
                            onFilterChange(filter.key, {
                              ...(filter.value || {}),
                              max: e.target.value,
                            })
                          }
                          className={`${inputClasses} flex-1`}
                          placeholder={filter.maxPlaceholder || 'Max'}
                          min={filter.min}
                          max={filter.max}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdvancedFilter;
